/** Ms without {@link IPlaybackTimerStuckDetectInput.playbackTiming} frameElapsedMs changing before flatline counts. */
export const PLAYBACK_TIMER_STUCK_FLATLINE_MS = 1500

/** Wall dwell must exceed scheduled slot duration by at least this much (ms). */
export const PLAYBACK_TIMER_STUCK_SLOT_OVERRUN_MARGIN_MS = 500

/** Treat frame elapsed as “at cap” within this many ms of duration. */
export const PLAYBACK_TIMER_STUCK_AT_CAP_EPSILON_MS = 50

export interface IPlaybackTimerStuckStatus {
  suspected: boolean
  /** Ms the visible frame has exceeded its scheduled slot (0 when not suspected). */
  overrunMs: number
  frameId: string | null
  /** Wall ms since the visible frame identity last changed. */
  visibleDwellMs: number
  /** Ms since playback timing frameElapsedMs last changed. */
  timingFlatlineMs: number
}

export interface IPlaybackTimerStuckDetectInput {
  nowMs: number
  documentVisible: boolean
  queueLength: number
  playbackTiming: {
    frameElapsedMs: number
    frameDurationMs: number
  } | null
  visibleFrame: { id: string } | null
  visibleFrameStartMs: number
  lastTimingFrameElapsedMs: number | null
  lastTimingChangeMs: number | null
  suppressBecauseRegistrationResync: boolean
  suppressBecauseRegistrationTransitionHold: boolean
}

const INACTIVE_STATUS: IPlaybackTimerStuckStatus = {
  suspected: false,
  overrunMs: 0,
  frameId: null,
  visibleDwellMs: 0,
  timingFlatlineMs: 0
}

/**
 * Detect when the timers overlay (scheduler readout) appears stuck: same visible frame past its slot,
 * frame elapsed flat or capped, and timing sample unchanged for {@link PLAYBACK_TIMER_STUCK_FLATLINE_MS}.
 */
export function detectPlaybackTimerStuck(input: IPlaybackTimerStuckDetectInput): IPlaybackTimerStuckStatus {
  const {
    nowMs,
    documentVisible,
    queueLength,
    playbackTiming,
    visibleFrame,
    visibleFrameStartMs,
    lastTimingFrameElapsedMs,
    lastTimingChangeMs,
    suppressBecauseRegistrationResync,
    suppressBecauseRegistrationTransitionHold
  } = input

  if (
    queueLength < 1
    || playbackTiming == null
    || visibleFrame == null
    || visibleFrameStartMs <= 0
  ) {
    return INACTIVE_STATUS
  }

  if (
    !documentVisible
    || suppressBecauseRegistrationResync
    || suppressBecauseRegistrationTransitionHold
  ) {
    return {
      ...INACTIVE_STATUS,
      frameId: visibleFrame.id,
      visibleDwellMs: Math.max(0, nowMs - visibleFrameStartMs)
    }
  }

  const frameDurationMs = playbackTiming.frameDurationMs
  if (frameDurationMs <= 0) {
    return {
      ...INACTIVE_STATUS,
      frameId: visibleFrame.id,
      visibleDwellMs: Math.max(0, nowMs - visibleFrameStartMs)
    }
  }

  const visibleDwellMs = Math.max(0, nowMs - visibleFrameStartMs)
  const timingFlatlineMs = lastTimingChangeMs != null
    ? Math.max(0, nowMs - lastTimingChangeMs)
    : 0
  const slotOverrunMs = visibleDwellMs - frameDurationMs
  const slotOverrun = slotOverrunMs > PLAYBACK_TIMER_STUCK_SLOT_OVERRUN_MARGIN_MS
  const timingFlatline = timingFlatlineMs >= PLAYBACK_TIMER_STUCK_FLATLINE_MS
  const atDurationCap = playbackTiming.frameElapsedMs
    >= frameDurationMs - PLAYBACK_TIMER_STUCK_AT_CAP_EPSILON_MS
  const elapsedUnchanged = lastTimingFrameElapsedMs != null
    && playbackTiming.frameElapsedMs === lastTimingFrameElapsedMs

  const suspected = slotOverrun
    && timingFlatline
    && (atDurationCap || elapsedUnchanged)

  return {
    suspected,
    overrunMs: suspected ? Math.max(0, slotOverrunMs) : 0,
    frameId: visibleFrame.id,
    visibleDwellMs,
    timingFlatlineMs
  }
}
